import { Button, IconButton, Switch, Tag, useAlert } from '@ads/components-react';

import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DropDownListViewModel, ExamLangListItemViewModel } from '@/shared/api/generated';
import { usePostApiV1ExamImportExamItemBatch } from '@/shared/api/generated';
import { Stepper } from '@/shared/ui/atoms';
import { useExamFormOptions } from '../../../hooks/useExamFormOptions';
import type { SelectableQuestion } from '../../../hooks/useExamQuestionSelection';
import { useExamWizard } from '../../../hooks/useExamWizard';
import { useToggleExamLangStatus } from '../../../hooks/useToggleExamLangStatus';
import type { ExamFormData, LessonPlan } from '../../../types/exam';
import { AddLanguageForm, type EditingLang } from '../AddLanguageForm';
import { ExamForm } from '../ExamForm';
import { ExamQuestionSelectionStep } from '../ExamQuestionSelectionStep';
import { ExamReviewStep } from '../ExamReviewStep';
import { LessonPlanSearchModal } from '../LessonPlanSearchModal';

const LANG_BADGE: Record<string, string> = {
  'pt-BR': 'BR',
  'en-US': 'EN',
  'es-ES': 'ES',
  'fr-FR': 'FR',
};

const LANG_BADGE_COLORS: Record<string, string> = {
  'pt-BR': 'bg-base-success-100 text-base-success-700',
  'en-US': 'bg-base-accent-100 text-base-accent-700',
  'es-ES': 'bg-base-warning-100 text-base-warning-700',
  'fr-FR': 'bg-base-danger-100 text-base-danger-700',
};

/** Rótulos padrão (retornados pelo backend) usados para pré-selecionar os selects na criação. */
const DEFAULT_GRADE_CRITERIA_LABEL = 'Mais alta';
const DEFAULT_REFERENCE_SHOW_BEFORE_LABEL = 'Sem Visualização';
const DEFAULT_REFERENCE_SHOW_AFTER_LABEL = 'Gabarito Total';

/** Normaliza rótulos para comparação tolerante a acentuação e caixa. */
function normalizeLabel(label: string | null | undefined): string {
  return (label ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim()
    .toLowerCase();
}

/** Retorna a `key` da opção cujo rótulo corresponde ao alvo, ou `null` se não houver correspondência. */
function findOptionKey(
  options: readonly DropDownListViewModel[],
  targetLabel: string,
): number | null {
  const target = normalizeLabel(targetLabel);
  return options.find(option => normalizeLabel(option.value) === target)?.key ?? null;
}

const WIZARD_STEPS = [
  { title: 'wizard.step4', description: '' },
  { title: 'wizard.step1', description: '' },
  { title: 'wizard.step2', description: '' },
  { title: 'wizard.step3', description: '' },
  { title: 'wizard.step5', description: '' },
] as const;

export function ExamWizard() {
  const { t } = useTranslation('examManagement');
  const { alert } = useAlert();
  const [step, setStep] = useState(0);

  const { examTypes, gradeCriteriaOptions, referenceShowOptions } = useExamFormOptions();
  const { createdExamId, examLangs, isSaving, saveExam, saveDraft } = useExamWizard();

  // ─── Step 1: Form data ──────────────────────────────────────────────────────
  const [formData, setFormData] = useState<ExamFormData>({
    title: '',
    instruction: '',
    examType: null,
    companyId: null,
    companyName: null,
    disciplineId: null,
    disciplineName: null,
    gradeCriteria: null,
    weight: 1,
    examReferenceShow: null,
    examReferenceShowPost: null,
    showAnswersTries: false,
    randomize: true,
    questionsQuantity: null,
    percentualComplexity: 0,
    recommendedToDoOnPortal: false,
    enableSafeEvaluationMode: false,
    maxScreenExitsAllowed: 3,
    enableBlockUnauthorizedActions: false,
  });

  const savedFormDataRef = useRef<ExamFormData | null>(null);

  // Pré-seleciona os selects de critério de nota e visualização de gabarito com os
  // valores padrão de negócio assim que as opções da API carregam (apenas na criação,
  // enquanto o campo ainda não foi definido).
  const defaultsAppliedRef = useRef(false);
  useEffect(() => {
    if (defaultsAppliedRef.current) return;
    if (gradeCriteriaOptions.length === 0 || referenceShowOptions.length === 0) return;

    defaultsAppliedRef.current = true;
    const gradeCriteria = findOptionKey(gradeCriteriaOptions, DEFAULT_GRADE_CRITERIA_LABEL);
    const examReferenceShow = findOptionKey(
      referenceShowOptions,
      DEFAULT_REFERENCE_SHOW_BEFORE_LABEL,
    );
    const examReferenceShowPost = findOptionKey(
      referenceShowOptions,
      DEFAULT_REFERENCE_SHOW_AFTER_LABEL,
    );

    setFormData(prev => ({
      ...prev,
      gradeCriteria: prev.gradeCriteria ?? gradeCriteria,
      examReferenceShow: prev.examReferenceShow ?? examReferenceShow,
      examReferenceShowPost: prev.examReferenceShowPost ?? examReferenceShowPost,
    }));
  }, [gradeCriteriaOptions, referenceShowOptions]);

  // ─── Step 2: Languages ──────────────────────────────────────────────────────
  const [editingLang, setEditingLang] = useState<EditingLang | null>(null);
  const { toggleStatus, pendingId: togglePendingId } = useToggleExamLangStatus(createdExamId ?? 0);

  // Rola até o formulário "Adicionar idioma" (posicionado abaixo da lista) ao
  // entrar em modo edição, para o admin não perder o formulário de vista.
  const addLanguageFormRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (editingLang !== null) {
      addLanguageFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [editingLang]);

  function handleEditLang(lang: ExamLangListItemViewModel) {
    setEditingLang({
      id: lang.id ?? 0,
      cultureInfo: lang.cultureInfo ?? '',
      active: lang.active ?? false,
    });
  }

  // ─── Step 3: Questions ──────────────────────────────────────────────────────
  const [selectedQuestions, setSelectedQuestions] = useState<SelectableQuestion[]>([]);
  const importQuestionsMutation = usePostApiV1ExamImportExamItemBatch({
    mutation: {
      onSuccess: () => alert({ alertType: 'success', title: t('toast.questionImported') }),
      onError: () => alert({ alertType: 'warning', title: t('toast.error') }),
    },
  });

  // ─── Step 4: Publications (linked plans) ─────────────────────────────────
  const [showLessonSearch, setShowLessonSearch] = useState(false);
  const [linkedPlans, setLinkedPlans] = useState<LessonPlan[]>([]);

  // ─── Navigation ─────────────────────────────────────────────────────────────
  const goNext = async () => {
    if (step === 1) {
      const isNew = createdExamId === null;
      const hasChanges =
        savedFormDataRef.current === null ||
        JSON.stringify(formData) !== JSON.stringify(savedFormDataRef.current);

      if (hasChanges) {
        const ok = await saveExam(formData);
        if (!ok) return;
        savedFormDataRef.current = { ...formData };
        if (isNew) {
          alert({ alertType: 'success', title: t('toast.createSuccess') });
        }
      }
    }
    if (step === 3 && selectedQuestions.length > 0 && examLangs.length > 0) {
      try {
        const items = examLangs
          .map(lang => {
            const ids = selectedQuestions
              .filter(q => q.cultureInfo === lang.cultureInfo)
              .map(q => Number(q.id));
            return lang.id && ids.length > 0 ? { examLangId: lang.id, examItemIds: ids } : null;
          })
          .filter((item): item is { examLangId: number; examItemIds: number[] } => item !== null);

        if (items.length > 0) {
          await importQuestionsMutation.mutateAsync({ data: { items } });
        }
      } catch {
        return;
      }
    }
    setStep(s => Math.min(s + 1, 4));
  };
  const goPrev = () => setStep(s => Math.max(s - 1, 0));

  const handleSelectPlans = (plans: readonly LessonPlan[]) => {
    setLinkedPlans(prev => [...prev, ...plans.filter(p => !prev.some(e => e.id === p.id))]);
    setShowLessonSearch(false);
  };

  const steps = WIZARD_STEPS.map(s => ({
    title: t(s.title as never),
    description: s.description,
  }));

  const examCultureInfos = examLangs.map(l => l.cultureInfo).filter((c): c is string => Boolean(c));

  const selectionByLang = examCultureInfos.map(
    lang => selectedQuestions.filter(q => q.cultureInfo === lang).length,
  );
  const nonZeroLangCounts = selectionByLang.filter(c => c > 0);
  const isSelectionBalanced =
    nonZeroLangCounts.length === 0 ||
    (nonZeroLangCounts.every(c => c === nonZeroLangCounts[0]) &&
      nonZeroLangCounts.length === examCultureInfos.length);

  const isNextDisabled =
    (step === 1 && (!formData.title.trim() || isSaving)) ||
    (step === 3 &&
      (importQuestionsMutation.isPending ||
        (selectedQuestions.length > 0 && !isSelectionBalanced)));

  return (
    <div className="flex flex-col gap-mini">
      <Stepper
        steps={steps}
        currentStep={step}
        onStepClick={idx => {
          if (idx <= step) setStep(idx);
        }}
      />

      {/** Step 1: Publication */}
      {step === 0 && (
        <div className="flex flex-col gap-mini">
          <h3 className="ts-subtitle-xxxs text-ctx-content-title">{t('publications.title')}</h3>
          <p className="ts-paragraph-xs text-ctx-content-base">{t('optional')}</p>

          <Button hierarchy="secondary" size="sm" onClick={() => setShowLessonSearch(true)}>
            {t('actions.searchLesson')}
          </Button>

          {showLessonSearch && (
            <LessonPlanSearchModal
              preselectedDisciplineId={formData.disciplineId}
              onSelect={handleSelectPlans}
              onCancel={() => setShowLessonSearch(false)}
            />
          )}

          {linkedPlans.length > 0 && (
            <div className="flex flex-col gap-mini">
              <span className="ts-subtitle-xxxs text-ctx-content-title">{t('linkedPlans')}</span>
              {linkedPlans.map(plan => (
                <div
                  key={plan.id}
                  className="flex items-center justify-between rounded-sm border border-ctx-layout-border bg-ctx-layout-surface p-xs"
                >
                  <span className="ts-paragraph-xs text-ctx-content-base">{plan.title}</span>
                  <span className="ts-caption-xxs text-ctx-content-base">{plan.classGroup}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/** Step 2: General Data */}
      {step === 1 && (
        <ExamForm
          form={formData}
          onUpdate={partial => setFormData(prev => ({ ...prev, ...partial }))}
        />
      )}

      {/** Step 3: Languages */}
      {step === 2 && (
        <div className="flex flex-col gap-mini">
          {examLangs.length > 0 && (
            <div className="overflow-hidden rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface shadow-level1">
              <div className="border-b-hairline border-ctx-layout-border px-sm py-mini">
                <h3 className="ts-subtitle-xs text-ctx-content-title">
                  {t('languages.list.title')}
                </h3>
                <p className="ts-paragraph-xxs text-ctx-content-base mt-quark">
                  {t('languages.list.subtitle', { count: examLangs.length })}
                </p>
              </div>

              <div className="flex flex-col">
                {examLangs.map((lang, idx) => (
                  <div
                    key={lang.id}
                    className={`flex items-center gap-xs px-sm py-xs ${idx > 0 ? 'border-t-hairline border-ctx-layout-border' : ''} ${editingLang?.id === lang.id ? 'bg-base-accent-50' : ''}`}
                  >
                    <span
                      className={`flex size-md shrink-0 items-center justify-center rounded-circular ts-subtitle-xxs !font-bold ${LANG_BADGE_COLORS[lang.cultureInfo ?? ''] ?? 'bg-base-gray-100 text-base-gray-700'}`}
                    >
                      {LANG_BADGE[lang.cultureInfo ?? ''] ?? '??'}
                    </span>

                    <span className="flex-1 ts-paragraph-xs text-ctx-content-title">
                      {lang.cultureInfoDisplay ?? lang.cultureInfo}
                    </span>

                    <Tag
                      label={
                        lang.active !== false
                          ? t('languages.list.statusActive')
                          : t('languages.list.statusDraft')
                      }
                      status={lang.active !== false ? 'positive' : 'helper'}
                      size="md"
                      custom={{ text: 'text-nowrap' }}
                    />

                    <IconButton
                      icon={PencilSquareIcon}
                      ariaLabel={t('languages.list.edit')}
                      hierarchy="tertiary"
                      size="md"
                      onClick={() => handleEditLang(lang)}
                    />

                    <Switch
                      checked={lang.active !== false}
                      size="sm"
                      disabled={togglePendingId === lang.id}
                      onCheckedChange={checked => {
                        if (!checked) {
                          toggleStatus(lang.id ?? 0, lang.cultureInfo ?? '', checked);
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div ref={addLanguageFormRef} className="scroll-mt-mini">
            <AddLanguageForm
              examId={createdExamId ?? 0}
              {...(editingLang !== null ? { editingLang } : {})}
              onSave={() => setEditingLang(null)}
              onCancel={() => setEditingLang(null)}
            />
          </div>
        </div>
      )}

      {/** Step 4: Questions from Library */}
      {step === 3 && (
        <ExamQuestionSelectionStep
          examLangId={examLangs[0]?.id ?? 0}
          examCultureInfos={examCultureInfos}
          onSelectionChange={setSelectedQuestions}
        />
      )}

      {/** Step 5: Review & Activation */}
      {step === 4 && (
        <ExamReviewStep
          formData={formData}
          examLangs={examLangs}
          selectedQuestions={selectedQuestions}
          linkedPlans={linkedPlans}
          examTypes={examTypes}
          gradeCriteriaOptions={gradeCriteriaOptions}
          onGoToStep={setStep}
        />
      )}

      {/** Navigation buttons */}
      <div className="flex items-center justify-between border-t border-ctx-layout-border pt-md">
        <Button hierarchy="tertiary" size="md" onClick={goPrev}>
          {step === 0 ? t('actions.cancel') : t('actions.previous')}
        </Button>
        <div className="flex gap-mini">
          {step === 4 ? (
            <Button hierarchy="primary" size="md" onClick={saveDraft}>
              {t('actions.finish')}
            </Button>
          ) : (
            <Button
              hierarchy="primary"
              size="md"
              onClick={() => void goNext()}
              disabled={isNextDisabled}
              isLoading={
                (isSaving && step === 1) || (importQuestionsMutation.isPending && step === 3)
              }
            >
              {t('actions.next')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
